import {useEffect, useMemo, useRef, useState, type JSX} from 'react'
import {type GuidType} from '../../values/generic-type-definitions'
import Styled from './vessel-selector-button.styled'
import {type Vessel} from './vessel-selector.types'
import {VesselSelectionSummary} from './vessel-selection-summary'
import {LightTheme} from '@/theme/light-theme'
import {DarkTheme} from '@/theme/dark-theme'
import {ThemeProvider} from 'styled-components'
import type {VesselTag} from '@/api/vessels/vessel-tags'
import {getVesselSummaryDisplayData} from './vessel-selector-helper'

export interface VesselSelectorButtonProps {
    label?: string
    description?: string
    placeholder?: string
    vesselsWithTags: Vessel[]
    selectedVesselIds: Set<GuidType>
    onChange: (selectedVesselIds: Set<GuidType>) => void
    onDisplayChange?: (
        isAllSelected: boolean,
        selectedVessels: Vessel[],
        selectedTags: VesselTag[],
    ) => void
    isLoading?: boolean
    isDisabled?: boolean
    className?: string
    onOpenChange?: (isOpen: boolean) => void
    id?: string
    background?: string
    granularMode?: boolean
    theme?: 'light' | 'dark'
}

export function VesselSelectorButton(props: VesselSelectorButtonProps): JSX.Element {
    const theme = props.theme === 'dark' ? DarkTheme : LightTheme
    const [isOpen, setIsOpen] = useState(false)

    const buttonRef = useRef<HTMLButtonElement>(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const buttonWidth = useMemo(() => buttonRef.current?.getBoundingClientRect().width, [isOpen])

    const {vesselsWithTags, selectedVesselIds, onDisplayChange} = props

    const {isAllSelected, selectedVessels, selectedTags} = useMemo(() => {
        return getVesselSummaryDisplayData(vesselsWithTags, selectedVesselIds)
    }, [vesselsWithTags, selectedVesselIds])

    useEffect(() => {
        if (onDisplayChange) {
            onDisplayChange(isAllSelected, selectedVessels, selectedTags)
        }
    }, [onDisplayChange, isAllSelected, selectedVessels, selectedTags])

    return (
        <ThemeProvider theme={theme}>
            <Styled.Root className={props.className}>
                {!!props.label && <Styled.Label>{props.label}</Styled.Label>}
                <Styled.DialogTrigger
                    isOpen={isOpen}
                    onOpenChange={(open) => {
                        setIsOpen(open)
                        props.onOpenChange?.(open)
                    }}
                >
                    <Styled.ComboBoxLikeButton
                        background={props.background}
                        ref={buttonRef}
                        aria-label="Vessel Selector Button"
                        isDisabled={props.isDisabled || props.isLoading}
                        data-loading={props.isLoading ? true : undefined}
                        isDark={props.theme === 'dark'}
                    >
                        <Styled.Content>
                            <VesselSelectionSummary
                                isAllSelected={isAllSelected}
                                isLoading={props.isLoading}
                                selectedVessels={selectedVessels}
                                selectedTags={selectedTags}
                                placeholder={props.placeholder}
                            />
                        </Styled.Content>
                        {!props.isLoading && <Styled.DropdownIcon isSelected={isOpen} />}
                    </Styled.ComboBoxLikeButton>
                    <Styled.Popover
                        width={buttonWidth}
                        offset={2}
                        isDark={props.theme === 'dark'}
                        containerPadding={0}
                    >
                        <div id="vessel-filter-popover">
                            <Styled.VesselSelector
                                vesselsWithTags={props.vesselsWithTags}
                                selectedVesselIds={props.selectedVesselIds}
                                onChange={onChange}
                                isDefaultExpanded
                                granularMode={props.granularMode}
                            />
                        </div>
                    </Styled.Popover>
                </Styled.DialogTrigger>
                {!!props.description && <Styled.Text>{props.description}</Styled.Text>}
            </Styled.Root>
        </ThemeProvider>
    )

    function onChange(selectedVesselIds: Set<GuidType>): void {
        props.onChange(selectedVesselIds)
    }
}
